import { Badge, Box, Group, Table, Text } from "@mantine/core";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { useMemo } from "react";
import { FONT, SECTION, TW } from "./theme";

const TeamCodesSection = ({
  codes,
  codeDefinitions,
  pdx,
  addedCodes,
  removedCodes,
  removedCodeDefinitions,
}: {
  codes: string[];
  codeDefinitions: Record<string, string>;
  pdx: string;
  addedCodes?: Set<string>;
  removedCodes?: string[];
  removedCodeDefinitions?: Record<string, string>;
}) => {
  // PDx first, then rest
  const sortedCodes = useMemo(() => {
    const pdxCode = codes.find((c) => c === pdx);
    const rest = codes.filter((c) => c !== pdx);
    return pdxCode ? [pdxCode, ...rest] : rest;
  }, [codes, pdx]);

  return (
    <Box>
      <Group gap={6} mb="sm">
        <IconPlus size={14} color={SECTION.confirmedTitle} />
        <Text
          size={`${FONT.xs}px`}
          fw={600}
          c={SECTION.confirmedTitle}
          td="underline"
        >
          Confirmed Codes ({codes.length})
        </Text>
      </Group>

      {sortedCodes.length > 0 ? (
        <Table
          striped
          highlightOnHover
          withRowBorders={false}
          verticalSpacing={4}
          horizontalSpacing="xs"
          styles={{
            table: { tableLayout: "fixed" },
            td: {
              fontSize: FONT.sm,
              paddingTop: 4,
              paddingBottom: 4,
            },
          }}
        >
          <Table.Tbody>
            {sortedCodes.map((code) => {
              const isPdx = code === pdx;
              const isAdded = addedCodes?.has(code);
              return (
                <Table.Tr
                  key={code}
                  style={
                    isAdded
                      ? { backgroundColor: TW.green50 }
                      : undefined
                  }
                >
                  <Table.Td style={{ width: 100 }}>
                    <Group gap={6} wrap="nowrap">
                      <Text
                        size={`${FONT.sm}px`}
                        fw={isPdx ? 600 : 400}
                        c={isAdded ? TW.green700 : TW.gray800}
                      >
                        {isAdded ? `+ ${code}` : code}
                      </Text>
                      {isPdx && (
                        <Badge
                          size="xs"
                          styles={{
                            root: {
                              backgroundColor: TW.blue700,
                              color: TW.white,
                              fontSize: 9,
                              fontWeight: 700,
                              paddingLeft: 4,
                              paddingRight: 4,
                              height: 14,
                              minWidth: 0,
                            },
                          }}
                        >
                          PDx
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text
                      size={`${FONT.sm}px`}
                      c={isAdded ? TW.green700 : TW.gray600}
                      lineClamp={1}
                    >
                      {codeDefinitions[code] || "-"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      ) : (
        <Text size={`${FONT.xs}px`} c={TW.gray500} pl={20} py={4}>
          No confirmed codes
        </Text>
      )}

      {/* Removed codes */}
      {removedCodes && removedCodes.length > 0 && (
        <Box mt="sm">
          <Group gap="xs" mb={6}>
            <IconMinus size={14} color={SECTION.removedTitle} />
            <Text size={`${FONT.xs}px`} fw={600} c={SECTION.removedTitle}>
              Removed ({removedCodes.length})
            </Text>
          </Group>
          <Table
            withRowBorders={false}
            verticalSpacing={4}
            horizontalSpacing="xs"
            styles={{
              table: { tableLayout: "fixed" },
              td: {
                fontSize: FONT.xs,
                paddingTop: 4,
                paddingBottom: 4,
              },
            }}
          >
            <Table.Tbody>
              {removedCodes.map((code) => (
                <Table.Tr
                  key={code}
                  style={{ backgroundColor: TW.red50 }}
                >
                  <Table.Td style={{ width: 120 }}>
                    <Text size={`${FONT.xs}px`} c={TW.red700}>
                      − {code}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size={`${FONT.xs}px`} c={TW.red600} lineClamp={1}>
                      {removedCodeDefinitions?.[code] ||
                        codeDefinitions[code] ||
                        "-"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default TeamCodesSection;
